import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../ui/Icon';

const TEXT_STYLE_FONT_CHOICES = {
  helvetica: 'Helvetica, Arial, sans-serif',
  times: '"Times New Roman", Times, serif',
  courier: '"Courier New", Courier, monospace'
};

const DEFAULT_WIDGET_TEXT_STYLE = {
  fontFamily: 'helvetica',
  fontSize: 12,
  color: '#1f2937',
  bold: false,
  italic: false,
  underline: false
};

const parseCheckboxValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (Array.isArray(value)) {
    if (!value.length) return false;
    return value.some((item) => parseCheckboxValue(item));
  }
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  if (['0', 'false', 'no', 'off', 'unchecked'].includes(normalized)) return false;
  return true;
};

const resolveCheckboxLabel = (widget = {}) => {
  const options = widget?.options || {};
  const firstValue = Array.isArray(options?.values) && options.values.length ? options.values[0] : '';
  return String(
    options?.label ||
      widget?.textValue ||
      firstValue ||
      options?.defaultValue ||
      options?.name ||
      widget?.label ||
      ''
  ).trim();
};

const normalizeBooleanFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return ['1', 'true', 'yes', 'on', 'required'].includes(normalized);
};

const normalizeHexColor = (value, fallback = DEFAULT_WIDGET_TEXT_STYLE.color) => {
  const normalized = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) return normalized.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const shortColor = normalized.slice(1);
    return `#${shortColor[0]}${shortColor[0]}${shortColor[1]}${shortColor[1]}${shortColor[2]}${shortColor[2]}`.toLowerCase();
  }
  return fallback;
};

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

const resolveWidgetTextStyle = (widget = {}) => {
  const options = widget?.options || {};
  const explicitStyle = widget?.textStyle || widget?.TextStyle || options?.textStyle || options?.TextStyle;
  const source =
    explicitStyle && typeof explicitStyle === 'object' && !Array.isArray(explicitStyle)
      ? explicitStyle
      : {
          fontFamily: widget?.fontFamily || widget?.font || options?.fontFamily || options?.font,
          fontSize: widget?.fontSize || options?.fontSize,
          color: widget?.color || widget?.textColor || options?.color || options?.textColor,
          bold: widget?.bold ?? widget?.isBold ?? options?.bold ?? options?.isBold,
          italic: widget?.italic ?? widget?.isItalic ?? options?.italic ?? options?.isItalic,
          underline:
            widget?.underline ?? widget?.isUnderline ?? options?.underline ?? options?.isUnderline
        };

  const normalizedFontFamily = String(source?.fontFamily || source?.font || '')
    .trim()
    .toLowerCase();
  const parsedFontSize = Number.parseInt(source?.fontSize, 10);

  return {
    fontFamily: TEXT_STYLE_FONT_CHOICES[normalizedFontFamily]
      ? normalizedFontFamily
      : DEFAULT_WIDGET_TEXT_STYLE.fontFamily,
    fontSize: Number.isFinite(parsedFontSize)
      ? clampNumber(parsedFontSize, 8, 48)
      : DEFAULT_WIDGET_TEXT_STYLE.fontSize,
    color: normalizeHexColor(source?.color || source?.textColor, DEFAULT_WIDGET_TEXT_STYLE.color),
    bold: normalizeBooleanFlag(source?.bold ?? source?.isBold),
    italic: normalizeBooleanFlag(source?.italic ?? source?.isItalic),
    underline: normalizeBooleanFlag(source?.underline ?? source?.isUnderline)
  };
};

const hasExplicitWidgetTextStyle = (widget = {}) => {
  if (!widget || typeof widget !== 'object') return false;
  const options = widget?.options || {};
  if (
    (widget?.textStyle && typeof widget.textStyle === 'object') ||
    (widget?.TextStyle && typeof widget.TextStyle === 'object') ||
    (options?.textStyle && typeof options.textStyle === 'object') ||
    (options?.TextStyle && typeof options.TextStyle === 'object')
  ) {
    return true;
  }
  const keys = [
    'fontFamily',
    'font',
    'fontSize',
    'color',
    'textColor',
    'bold',
    'isBold',
    'italic',
    'isItalic',
    'underline',
    'isUnderline'
  ];
  return keys.some((key) => widget?.[key] !== undefined || options?.[key] !== undefined);
};

const buildTextStyle = (textStyle, zoomScale, fallback = {}) => ({
  fontFamily: TEXT_STYLE_FONT_CHOICES[textStyle.fontFamily] || TEXT_STYLE_FONT_CHOICES.helvetica,
  fontSize: `${Math.round(clampNumber(textStyle.fontSize * (Number(zoomScale) || 1), 8, 56))}px`,
  color: textStyle.color,
  fontWeight: textStyle.bold ? 700 : fallback.fontWeight || 500,
  fontStyle: textStyle.italic ? 'italic' : fallback.fontStyle || 'normal',
  textDecoration: textStyle.underline ? 'underline' : 'none'
});

const isWidgetRequired = (widget = {}, state = {}) => {
  const status = String(widget?.options?.status || '').trim().toLowerCase();
  if (status === 'required') return true;
  if (status === 'optional') return false;
  return (
    normalizeBooleanFlag(state?.required) ||
    normalizeBooleanFlag(widget?.required) ||
    normalizeBooleanFlag(widget?.options?.required) ||
    normalizeBooleanFlag(widget?.options?.validation?.required)
  );
};

/**
 * InteractiveWidget - Base component for all interactive widget types
 * Handles positioning, visual states, and click interactions
 * 
 * @param {Object} props
 * @param {Object} props.widget - Widget data (type, position, dimensions, options)
 * @param {number} props.pageWidth - PDF page width for position calculation
 * @param {number} props.zoomScale - Current zoom scale
 * @param {Object} props.widgetState - Current widget state from useWidgetState
 * @param {Function} props.onWidgetClick - Handler for widget click
 * @param {Function} props.onWidgetChange - Handler for widget value change
 * @param {boolean} props.isActive - Whether this widget is currently active
 * @param {string} props.widgetId - Unique widget identifier
 * @param {boolean} props.isReadOnly - Whether the widget is read-only for current signer
 */
function InteractiveWidget({
  widget,
  pageWidth,
  pageHeight,
  pageOriginalWidth,
  pageOriginalHeight,
  zoomScale,
  widgetState,
  onWidgetClick,
  onWidgetChange,
  isActive = false,
  widgetId,
  isReadOnly = false
}) {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();

  const toFiniteNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const isNormalizedCoordinateSet = (x, y, width, height) =>
    [x, y, width, height].every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 1
    );

  const rawX = toFiniteNumber(widget?.xPosition, 0);
  const rawY = toFiniteNumber(widget?.yPosition, 0);
  const rawWidth = toFiniteNumber(widget?.Width, 0.2);
  const rawHeight = toFiniteNumber(widget?.Height, 0.08);
  const hasNormalizedCoords = isNormalizedCoordinateSet(rawX, rawY, rawWidth, rawHeight);

  const safePageWidth = Number.isFinite(Number(pageWidth)) && Number(pageWidth) > 0 ? Number(pageWidth) : 1;
  const safePageHeight = Number.isFinite(Number(pageHeight)) && Number(pageHeight) > 0
    ? Number(pageHeight)
    : safePageWidth * 1.4142;
  const safeOriginalWidth =
    Number.isFinite(Number(pageOriginalWidth)) && Number(pageOriginalWidth) > 0
      ? Number(pageOriginalWidth)
      : safePageWidth;
  const safeOriginalHeight =
    Number.isFinite(Number(pageOriginalHeight)) && Number(pageOriginalHeight) > 0
      ? Number(pageOriginalHeight)
      : safePageHeight;
  const legacyScaleX = safePageWidth / safeOriginalWidth;
  const legacyScaleY = safePageHeight / safeOriginalHeight;

  const xAtBaseScale = hasNormalizedCoords ? rawX * safePageWidth : rawX * legacyScaleX;
  const yAtBaseScale = hasNormalizedCoords ? rawY * safePageHeight : rawY * legacyScaleY;
  const widthAtBaseScale = hasNormalizedCoords
    ? rawWidth * safePageWidth
    : rawWidth * legacyScaleX;
  const heightAtBaseScale = hasNormalizedCoords
    ? rawHeight * safePageHeight
    : rawHeight * legacyScaleY;

  // Calculate widget position and dimensions
  const xPos = xAtBaseScale * zoomScale;
  const yPos = yAtBaseScale * zoomScale;
  const widgetWidth = widthAtBaseScale * zoomScale;
  const widgetHeight = heightAtBaseScale * zoomScale;

  // Get widget state
  const state = widgetState || {};
  const isCompleted = state.completed || false;
  const isRequired = isWidgetRequired(widget, state);
  const value = state.value;
  const isReadOnlyWidget =
    Boolean(isReadOnly) || state.isPrefill === true || widget?.isPrefill === true || widget?.options?.isPrefill === true;
  const hasValue = value !== null && value !== undefined && value !== '';
  const isImageValue =
    typeof value === 'string' && (value.startsWith('data:') || /^https?:\/\//i.test(value));
  const hasCustomTextStyle = hasExplicitWidgetTextStyle(widget);
  const textStyle = hasCustomTextStyle ? resolveWidgetTextStyle(widget) : DEFAULT_WIDGET_TEXT_STYLE;

  // Determine border color based on state
  const getBorderColor = () => {
    if (isReadOnlyWidget) return 'border-slate-400';
    if (isCompleted) return 'border-green-500';
    if (isRequired) return 'border-red-500';
    return 'border-blue-500';
  };

  // Determine background color based on state and hover
  const getBackgroundColor = () => {
    // Initials should have transparent background
    if (widget.type === 'initials') return 'bg-transparent';
    if (isReadOnlyWidget) return 'bg-slate-100/75';
    
    if (isCompleted) return 'bg-green-50/30';
    if (isHovered) return 'bg-blue-100/50';
    return 'bg-blue-50/30';
  };

  // Handle widget click
  const handleClick = () => {
    if (isReadOnlyWidget) {
      return;
    }
    if (onWidgetClick) {
      onWidgetClick(widgetId, widget.type);
    }
  };

  // Render widget value (both completed and prefilled)
  const renderValue = () => {
    const widgetType = widget.type || 'signature';

    // Checkbox labels should always be visible, even before interaction.
    if (widgetType === 'checkbox') {
      const checkboxLabel = resolveCheckboxLabel(widget);
      const isChecked = parseCheckboxValue(value);
      return (
        <div className="absolute inset-0 flex items-center gap-1 px-1 pointer-events-none">
          <div className="flex-shrink-0">
            {isChecked ? (
              <Icon name="check" className="h-5 w-5 text-green-600" />
            ) : (
              <div className="h-4 w-4 border-2 border-gray-400 rounded bg-white" />
            )}
          </div>
          {checkboxLabel ? (
            <span className="text-xs text-gray-700 truncate flex-1">
              {checkboxLabel}
            </span>
          ) : null}
        </div>
      );
    }

    // Show value if completed OR if there's a prefilled value
    if (!hasValue) return null;

    // For signature/initials/stamp, show image (only if completed)
    if (widgetType === 'signature' || widgetType === 'initials' || widgetType === 'stamp') {
      // Show image when completed and source is data URL or remote URL
      if (isCompleted && isImageValue) {
        return (
          <img
            src={value}
            alt={widgetType}
            className="absolute inset-0 w-full h-full object-contain p-1 pointer-events-none"
          />
        );
      }
      // For text initials (prefilled), show as text
      if (widgetType === 'initials' && typeof value === 'string' && !value.startsWith('data:')) {
        return (
          <span
            className={`absolute inset-0 flex items-center justify-center px-2 text-sm pointer-events-none ${
              hasCustomTextStyle ? '' : 'font-semibold text-blue-700 italic'
            }`}
            style={
              hasCustomTextStyle
                ? buildTextStyle(textStyle, zoomScale, {
                    fontStyle: !isCompleted ? 'italic' : 'normal',
                    fontWeight: isCompleted ? 600 : 500
                  })
                : undefined
            }
          >
            {value}
          </span>
        );
      }
      return null;
    }

    // For text-based widgets, show text value (both completed and prefilled)
    if (widgetType === 'text' || widgetType === 'name' || widgetType === 'email' || 
        widgetType === 'job title' || widgetType === 'company') {
      return (
        <span
          className={`absolute inset-0 flex items-center justify-center px-2 text-sm truncate pointer-events-none ${
            hasCustomTextStyle ? '' : isCompleted ? 'text-gray-800 font-medium' : 'text-blue-700 italic'
          }`}
          style={
            hasCustomTextStyle
              ? buildTextStyle(textStyle, zoomScale, {
                  fontStyle: !isCompleted ? 'italic' : 'normal',
                  fontWeight: isCompleted ? 500 : 400
                })
              : undefined
          }
        >
          {value}
        </span>
      );
    }

    // For radio, show filled circle
    if (widgetType === 'radio') {
      return value ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="h-3 w-3 rounded-full bg-blue-600" />
        </div>
      ) : null;
    }

    // For date, show formatted date
    if (widgetType === 'date') {
      return (
        <span
          className={`absolute inset-0 flex items-center justify-center px-2 text-sm pointer-events-none ${
            hasCustomTextStyle ? '' : isCompleted ? 'text-gray-800 font-medium' : 'text-blue-700 italic'
          }`}
          style={
            hasCustomTextStyle
              ? buildTextStyle(textStyle, zoomScale, {
                  fontStyle: !isCompleted ? 'italic' : 'normal',
                  fontWeight: isCompleted ? 500 : 400
                })
              : undefined
          }
        >
          {value}
        </span>
      );
    }

    // For image, show image
    if (widgetType === 'image') {
      if (isImageValue) {
        return (
          <img
            src={value}
            alt="uploaded"
            className="absolute inset-0 w-full h-full object-contain p-1 pointer-events-none"
          />
        );
      }
      return null;
    }

    // For dropdown, show selected value
    if (widgetType === 'dropdown') {
      return (
        <span
          className={`absolute inset-0 flex items-center justify-center px-2 text-sm truncate pointer-events-none ${
            hasCustomTextStyle ? '' : isCompleted ? 'text-gray-800' : 'text-blue-700 italic'
          }`}
          style={
            hasCustomTextStyle
              ? buildTextStyle(textStyle, zoomScale, {
                  fontStyle: !isCompleted ? 'italic' : 'normal',
                  fontWeight: isCompleted ? 500 : 400
                })
              : undefined
          }
        >
          {value}
        </span>
      );
    }

    return null;
  };

  return (
    <div
      className={`absolute border-2 rounded transition-all ${getBorderColor()} ${getBackgroundColor()} ${
        isActive ? 'ring-4 ring-blue-300 animate-pulse' : ''
      } ${isReadOnlyWidget ? 'cursor-default' : 'cursor-pointer'}`}
      style={{
        left: `${xPos}px`,
        top: `${yPos}px`,
        width: `${widgetWidth}px`,
        height: `${widgetHeight}px`,
        minWidth: '40px',
        minHeight: '16px'
      }}
      onClick={handleClick}
      onMouseEnter={() => {
        if (!isReadOnlyWidget) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => setIsHovered(false)}
      title={
        isReadOnlyWidget
          ? `${widget.type || 'text'} - Prefilled (read-only)`
          : `${widget.type || 'signature'} - ${isRequired ? 'Required' : 'Optional'} - Click to ${isCompleted ? 'edit' : 'fill'}`
      }
      role="button"
      tabIndex={isReadOnlyWidget ? -1 : 0}
      aria-label={
        isReadOnlyWidget
          ? `${widget.type || 'text'} field, prefilled and read-only`
          : `${widget.type || 'signature'} field, ${isRequired ? 'required' : 'optional'}, ${isCompleted ? 'completed' : 'empty'}`
      }
      onKeyDown={(e) => {
        if (isReadOnlyWidget) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Render widget value if completed */}
      {renderValue()}

      {/* Show completion checkmark overlay */}
      {isCompleted && !isReadOnlyWidget && (
        <div className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-green-500 flex items-center justify-center shadow-md pointer-events-none">
          <Icon name="check" className="h-4 w-4 text-white" />
        </div>
      )}

      {/* Show placeholder text when empty (no value at all) */}
      {!hasValue && widget?.type !== 'checkbox' && (
        <span className="absolute inset-0 flex items-center justify-center text-xs text-blue-400 italic pointer-events-none">
          {widget.type === 'text'
            ? (widget.placeholderText || widget.options?.placeholderText || t('new-ui.individual-signing.placeholder-text-hint', { defaultValue: 'Type here...' }))
            : t(`new-ui.widget-type.${widget.type || 'signature'}`, { defaultValue: widget.type || 'signature' })}
        </span>
      )}

      {/* Show required indicator */}
      {isRequired && !isCompleted && !isReadOnlyWidget && (
        <span className="absolute top-0 right-0 text-red-500 text-xs font-bold pointer-events-none">
          *
        </span>
      )}
      {isReadOnlyWidget && (
        <span className="absolute bottom-0 right-0 rounded-tl bg-slate-600 px-1 py-[1px] text-[9px] font-semibold uppercase tracking-[0.04em] text-white pointer-events-none">
          Prefill
        </span>
      )}
    </div>
  );
}

export default InteractiveWidget;
